/**
 * Journal.js
 * Main journal interface for Constellation KG
 *
 * Two-panel layout:
 * - Left: Table of existing journal entries (date, title, type, actions)
 * - Right: Form to create new entries or edit existing ones (title, image drop, content, tags)
 *
 * Features:
 * - View all journal entries sorted by date (newest first)
 * - Click an entry to load it for editing
 * - Create new entries via the form panel
 * - Delete entries via trash icon
 * - Dynamic Save/Update button based on mode
 * - Speech-to-text for My Thoughts input
 *
 * Follows the same layout patterns as AIExplorerPage.js
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { journalService } from '../../services/api';
import { useJournalSearch } from '../../contexts/JournalSearchContext';
import { useSpeechRecognitionContext } from '../../contexts/SpeechRecognitionContext';
import MicrophoneButton from '../common/MicrophoneButton/MicrophoneButton';
import './Journal.css';

// =========================================
// Constants
// =========================================
const MAX_CONTENT_LENGTH = 3000;
const MAX_TITLE_LENGTH = 200;
const MAX_TAGS_LENGTH = 100;
const MAX_IMAGE_DIMENSION = 1024; // Max pixels on longest side
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// =========================================
// Entry Type Icons
// =========================================
const EntryTypeIcon = ({ type }) => {
  if (type === 'text_and_image' || type === 'image') {
    return (
      <span className="entry-type-icon" title="Image + Text">
        📷
      </span>
    );
  }
  return (
    <span className="entry-type-icon" title="Text">
      📝
    </span>
  );
};

// =========================================
// Main Component
// =========================================
const JournalPage = () => {
  // =========================================
  // State Management
  // =========================================
  
  // Entry list state
  const [entries, setEntries] = useState([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [entriesError, setEntriesError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  // Active sidebar search query. Journal owns its entry list/pagination; this
  // context only supplies the query string set from the sidebar accordion.
  const { query: journalSearchQuery } = useJournalSearch();

  // Selected entry for editing (null = creating new entry)
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: '',
    imagePath: null,
    imageMetadata: null
  });

  // Image state (separate from formData for cleaner handling)
  const [imageData, setImageData] = useState({
    base64: null,       // Compressed image data
    filename: null,     // Original filename
    mimeType: null,     // Image MIME type
    previewUrl: null,   // For thumbnail display
    sourceUrl: '',      // Canonical source URL (e.g., arxiv link)
    discoveredVia: ''   // Where user found it (e.g., Instagram post)
  });

  // Form UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', or null
  const [saveError, setSaveError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // =========================================
  // Refs
  // =========================================
  
  // Reference to the content textarea for speech-to-text
  const contentTextareaRef = useRef(null);

  // Mirror of the active search query so loadEntries' existing callers
  // (page change, save, delete, retry) keep the filter without threading the
  // query through every call site. Kept in sync by the load effect below.
  const searchQueryRef = useRef('');

  // =========================================
  // Speech Recognition
  // =========================================
  
  const { 
    isEnabled: sttEnabled,
    isRecording,
    statusMessage: sttStatusMessage,
    stopRecording
  } = useSpeechRecognitionContext();

  // Handle transcript changes from speech recognition
  const handleTranscriptChange = useCallback((text) => {
    setFormData(prev => ({
      ...prev,
      content: text
    }));
  }, []);

  // Stop recording when saving starts
  useEffect(() => {
    if (isSaving && isRecording) {
      stopRecording();
    }
  }, [isSaving, isRecording, stopRecording]);

  // =========================================
  // Computed Values
  // =========================================
  
  // Determine if we're in edit mode
  const isEditMode = selectedEntry !== null;
  
  // Dynamic button text
  const getButtonText = () => {
    if (isSaving) return isEditMode ? 'Updating...' : 'Saving...';
    if (saveStatus === 'success') return isEditMode ? 'Updated!' : 'Saved!';
    return isEditMode ? 'Update' : 'Save';
  };

  // =========================================
  // Data Loading
  // =========================================
  
  const loadEntries = useCallback(async (page = 1, search = searchQueryRef.current) => {
    setIsLoadingEntries(true);
    setEntriesError(null);

    try {
      const response = await journalService.getAll({ page, limit: 25, search });
      setEntries(response.entries || []);
      setPagination({
        page: response.pagination?.page || 1,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0
      });
    } catch (error) {
      console.error('Error loading journal entries:', error);
      setEntriesError('Failed to load journal entries');
      setEntries([]);
    } finally {
      setIsLoadingEntries(false);
    }
  }, []);

  // Load entries on mount and whenever the sidebar search query changes.
  // A new search or a clear resets to page 1; the ref keeps later
  // page/save/delete reloads scoped to the active query.
  useEffect(() => {
    searchQueryRef.current = journalSearchQuery;
    loadEntries(1, journalSearchQuery);
  }, [journalSearchQuery, loadEntries]);

  // =========================================
  // Entry Selection Handlers
  // =========================================

  /**
   * Handle clicking on an entry row to load it for editing
   */
  const handleSelectEntry = (entry) => {
    // Stop any active recording when selecting a different entry
    if (isRecording) {
      stopRecording();
    }

    setSelectedEntry(entry);
    setFormData({
      title: entry.title || '',
      content: entry.content || '',
      tags: entry.tags?.original || '',
      imagePath: entry.imagePath || null,
      imageMetadata: entry.imageMetadata || null
    });

    // Determine if this entry has an existing image attached
    // Check entryType or imageMetadata presence as source of truth
    const hasExistingImage =
      entry.entryType === 'image' ||
      entry.entryType === 'text_and_image' ||
      !!entry.imageMetadata;

    // Set imageData state - preserve filename for existing images to show indicator
    setImageData({
      base64: null,  // No new image data (not re-uploading)
      filename: hasExistingImage
        ? (entry.imageMetadata?.filename || entry.imagePath || 'Attached image')
        : null,
      mimeType: entry.imageMetadata?.mimeType || null,
      previewUrl: null,  // No preview for existing images
      sourceUrl: entry.imageMetadata?.sourceUrl || '',
      discoveredVia: entry.imageMetadata?.discoveredVia || ''
    });

    // Clear any previous save status
    setSaveStatus(null);
    setSaveError(null);
  };

  /**
   * Clear form and switch to "new entry" mode
   */
  const handleNewEntry = () => {
    // Stop any active recording when creating new entry
    if (isRecording) {
      stopRecording();
    }

    setSelectedEntry(null);
    setFormData({
      title: '',
      content: '',
      tags: '',
      imagePath: null,
      imageMetadata: null
    });

    // Clear image state
    setImageData({
      base64: null,
      filename: null,
      mimeType: null,
      previewUrl: null,
      sourceUrl: '',
      discoveredVia: ''
    });

    setSaveStatus(null);
    setSaveError(null);
  };

  // =========================================
  // Form Handlers
  // =========================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear save status when user starts editing
    if (saveStatus) {
      setSaveStatus(null);
      setSaveError(null);
    }
  };

  // Handler for image URL fields (sourceUrl and discoveredVia)
  const handleImageUrlChange = (e) => {
    const { name, value } = e.target;
    setImageData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear save status when user starts editing
    if (saveStatus) {
      setSaveStatus(null);
      setSaveError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setSaveError('Please enter a title for your entry');
      setSaveStatus('error');
      return;
    }
    
    if (!formData.content.trim()) {
      setSaveError('Please enter content for your entry');
      setSaveStatus('error');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);
    setSaveError(null);

    try {
      const entryData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        tags: formData.tags.trim()
      };

      // Include image data if present (new image being added)
      if (imageData.base64) {
        entryData.imageData = imageData.base64;
        entryData.imageFilename = imageData.filename;
      }

      // Include image URL fields if we have an image (new or existing)
      if (imageData.base64 || imageData.filename) {
        entryData.imageSourceUrl = imageData.sourceUrl?.trim() || '';
        entryData.imageDiscoveredVia = imageData.discoveredVia?.trim() || '';
      }

      if (isEditMode) {
        // Update existing entry
        await journalService.update(selectedEntry.journalEntryId, entryData);
        setSaveStatus('success');
        
        // Reload entries to show the updated one
        await loadEntries(pagination.page);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      } else {
        // Create new entry
        await journalService.create(entryData);

        // Success - clear form and reload entries
        setFormData({
          title: '',
          content: '',
          tags: '',
          imagePath: null,
          imageMetadata: null
        });

        // Clear image state
        setImageData({
          base64: null,
          filename: null,
          mimeType: null,
          previewUrl: null
        });

        setSaveStatus('success');

        // Reload entries to show the new one
        await loadEntries(1);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      }

    } catch (error) {
      console.error('Error saving journal entry:', error);
      setSaveError(
        error.response?.data?.errors?.join(', ') || 
        error.response?.data?.message || 
        'Failed to save entry. Please try again.'
      );
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // =========================================
  // Image Compression and Drop Handlers
  // =========================================

  /**
   * Compress an image to max dimensions using canvas
   * @param {File} file - Image file to compress
   * @returns {Promise<{base64: string, mimeType: string}>}
   */
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Calculate new dimensions
          let { width, height } = img;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
              height = MAX_IMAGE_DIMENSION;
            }
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 (use JPEG for better compression, PNG for transparency)
          const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          const quality = outputType === 'image/jpeg' ? 0.85 : undefined;
          const base64 = canvas.toDataURL(outputType, quality);

          resolve({
            base64,
            mimeType: outputType,
            width,
            height
          });
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];

      // Check if it's a supported image type
      if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
        setSaveError('Please drop a supported image file (JPG, PNG, GIF, or WebP)');
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus(null);
          setSaveError(null);
        }, 3000);
        return;
      }

      try {
        // Compress the image
        const compressed = await compressImage(file);

        // Store the compressed image data
        setImageData(prev => ({
          base64: compressed.base64,
          filename: file.name,
          mimeType: compressed.mimeType,
          previewUrl: compressed.base64, // Use base64 as preview
          sourceUrl: prev.sourceUrl || '',      // Preserve if already entered
          discoveredVia: prev.discoveredVia || '' // Preserve if already entered
        }));

        // Clear any previous error
        if (saveStatus === 'error') {
          setSaveStatus(null);
          setSaveError(null);
        }

        console.log('Image compressed:', {
          originalName: file.name,
          originalSize: Math.round(file.size / 1024) + 'KB',
          compressedSize: Math.round(compressed.base64.length * 0.75 / 1024) + 'KB',
          dimensions: `${compressed.width}x${compressed.height}`
        });

      } catch (error) {
        console.error('Image compression failed:', error);
        setSaveError('Failed to process image. Please try again.');
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus(null);
          setSaveError(null);
        }, 3000);
      }
    }
  };

  const handleRemoveImage = () => {
    // Revoke object URL if it exists (for memory cleanup)
    if (imageData.previewUrl && imageData.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageData.previewUrl);
    }

    setImageData({
      base64: null,
      filename: null,
      mimeType: null,
      previewUrl: null,
      sourceUrl: '',
      discoveredVia: ''
    });

    // Also clear from formData if editing an existing entry with image
    setFormData(prev => ({
      ...prev,
      imagePath: null,
      imageMetadata: null
    }));
  };

  // =========================================
  // Delete Handler
  // =========================================

  const handleDeleteEntry = async (entryId, e) => {
    // Stop propagation to prevent row click from firing
    if (e) {
      e.stopPropagation();
    }
    
    if (!window.confirm('Are you sure you want to delete this journal entry?')) {
      return;
    }

    try {
      await journalService.delete(entryId);
      
      // If we deleted the currently selected entry, clear the form
      if (selectedEntry && selectedEntry.journalEntryId === entryId) {
        handleNewEntry();
      }
      
      // Reload entries after deletion
      await loadEntries(pagination.page);
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  // =========================================
  // Pagination Handler
  // =========================================

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadEntries(newPage);
    }
  };

  // =========================================
  // Render Helpers
  // =========================================

  const renderEntriesTable = () => {
    if (isLoadingEntries) {
      return (
        <div className="journal-loading">
          <p>Loading journal entries...</p>
        </div>
      );
    }

    if (entriesError) {
      return (
        <div className="journal-error">
          <p>{entriesError}</p>
          <button onClick={() => loadEntries()} className="retry-button">
            Retry
          </button>
        </div>
      );
    }

    if (entries.length === 0) {
      return (
        <div className="journal-empty">
          <p>No journal entries yet.</p>
          <p className="journal-empty-hint">
            Create your first entry using the form on the right.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="journal-table-wrapper">
          <table className="journal-table">
            <thead>
              <tr>
                <th className="col-date">Date</th>
                <th className="col-title">Title</th>
                <th className="col-type">Type</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr
                  key={entry.journalEntryId}
                  onClick={() => handleSelectEntry(entry)}
                  className={selectedEntry?.journalEntryId === entry.journalEntryId ? 'selected' : ''}
                >
                  <td className="col-date">{entry.formattedDate}</td>
                  <td className="col-title">
                    <span className="entry-title" title={entry.title}>
                      {entry.title}
                    </span>
                  </td>
                  <td className="col-type">
                    <EntryTypeIcon type={entry.entryType} />
                  </td>
                  <td className="col-actions">
                    <button
                      className="delete-button"
                      onClick={(e) => handleDeleteEntry(entry.journalEntryId, e)}
                      title="Delete entry"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination — sibling of the scroll wrapper, pinned at the panel bottom */}
        {pagination.totalPages > 1 && (
          <div className="journal-pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              Next →
            </button>
          </div>
        )}
      </>
    );
  };

  // =========================================
  // Main Render
  // =========================================

  return (
    <div className="journal-page">
      {/* Left Panel - Entries Table */}
      <div className="journal-entries-panel">
        <div className="journal-entries-header">
          <h2>Journal Entries</h2>
          <span className="entry-count">
            {pagination.total} {pagination.total === 1 ? 'entry' : 'entries'}
          </span>
        </div>
        <div className="journal-results-container">
          {renderEntriesTable()}
        </div>
      </div>

      {/* Right Panel - Entry Form */}
      <div className="journal-form-panel">
        {/* New Entry Button - only shown when editing an existing entry */}
        {isEditMode && (
          <button 
            type="button"
            className="new-entry-button"
            onClick={handleNewEntry}
          >
            + New Entry
          </button>
        )}

        <form onSubmit={handleSubmit} className="journal-form">
          {/* Title Input */}
          <div className="form-group">
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Type a title for this entry..."
              maxLength={MAX_TITLE_LENGTH}
              className="title-input"
              disabled={isSaving}
            />
          </div>

          {/* Image Drop Zone */}
          <div
            className={`image-drop-zone ${isDragOver ? 'drag-over' : ''} ${imageData.base64 || imageData.filename ? 'has-image' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {imageData.base64 ? (
              // New image being added - show thumbnail preview
              <div className="image-preview">
                <img
                  src={imageData.previewUrl}
                  alt="Preview"
                  className="image-thumbnail"
                />
                <span className="image-filename">{imageData.filename}</span>
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={handleRemoveImage}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ) : imageData.filename ? (
              // Existing image on an entry being edited (already processed, can't re-upload)
              <div className="image-preview">
                <span className="image-filename">📷 {imageData.filename}</span>
                {/* No remove button for existing processed images */}
              </div>
            ) : (
              <p className="drop-zone-text">
                Drag and drop a photo here for processing (optional)
              </p>
            )}
          </div>

          {/* Image URL Fields - only visible when image is attached */}
          {(imageData.base64 || imageData.filename) && (
            <div className="image-url-fields">
              <input
                type="text"
                name="sourceUrl"
                value={imageData.sourceUrl}
                onChange={handleImageUrlChange}
                placeholder="Source URL (optional) - link to the original content"
                className="image-url-input"
                disabled={isSaving}
              />
              <input
                type="text"
                name="discoveredVia"
                value={imageData.discoveredVia}
                onChange={handleImageUrlChange}
                placeholder="Discovered via (optional) - where you found this"
                className="image-url-input"
                disabled={isSaving}
              />
              {/* Display saved URL links */}
              {(imageData.sourceUrl || imageData.discoveredVia) && (
                <div className="image-url-links">
                  {imageData.sourceUrl && (
                    <a
                      href={imageData.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="image-url-link"
                      title={imageData.sourceUrl}
                    >
                      🔗 Source
                    </a>
                  )}
                  {imageData.discoveredVia && (
                    <a
                      href={imageData.discoveredVia}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="image-url-link"
                      title={imageData.discoveredVia}
                    >
                      🔗 Via
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Content Textarea with Mic Button */}
          <div className="form-group">
            <div className="form-label-header">
              <label htmlFor="content" className="form-label">
                My Thoughts
              </label>
              {sttEnabled && (
                <MicrophoneButton
                  textareaRef={contentTextareaRef}
                  maxLength={MAX_CONTENT_LENGTH}
                  onTranscriptChange={handleTranscriptChange}
                />
              )}
            </div>
            <span className="character-count">
              {formData.content.length} / {MAX_CONTENT_LENGTH} characters
            </span>
            <textarea
              id="content"
              name="content"
              ref={contentTextareaRef}
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Type your thoughts here... Like a Thought on a Knowledge Entry, it will be stored in a way where only you can access it"
              maxLength={MAX_CONTENT_LENGTH}
              className="content-textarea"
              disabled={isSaving}
              rows={10}
            />
            {/* THE DICTATION STATUS LINE — full width, below the field.
                The `isRecording` condition was dropped on October 13, 2026.
                "Listening..." was the only message that ever reached this line,
                so every refusal and failure fell back to MicrophoneButton's own
                status, which is an inline column beside the field capped at
                150px: the text wrapped into a narrow strip and pushed the field
                down. The button no longer renders a status at all, and this
                line now shows whatever the hook has to say — listening,
                preparing, retryable failure, permission, permanent refusal. The
                hook clears the message to '' when idle, so the `&&` is what
                hides it. */}
            {sttEnabled && sttStatusMessage && (
              <div className="stt-status-message">{sttStatusMessage}</div>
            )}
          </div>

          {/* Tags Input */}
          <div className="form-group">
            <label htmlFor="tags" className="form-label">
              Tags
              <span className="character-count">
                {formData.tags.length} / {MAX_TAGS_LENGTH} characters
              </span>
            </label>
            <textarea
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="Separate your tags with commas..."
              maxLength={MAX_TAGS_LENGTH}
              className="tags-input"
              disabled={isSaving}
              rows={3}
            />
          </div>

          {/* Save/Update Button */}
          <div className="form-actions">
            <button
              type="submit"
              className={`save-button ${saveStatus === 'success' ? 'success' : ''} ${saveStatus === 'error' ? 'error' : ''}`}
              disabled={isSaving || !formData.title.trim() || !formData.content.trim()}
            >
              {getButtonText()}
            </button>
          </div>

          {/* Error Message */}
          {saveStatus === 'error' && saveError && (
            <div className="save-error">
              {saveError}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default JournalPage;
